import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";

import { AuthParams, AuthUserState } from "globalConstants";
import { checkTokenExpiration } from "helpers";
import { useFusionAuthTokenMutation } from "queries";
import { fusionAuthService } from "services";
import { uiActions } from "store";
import { getCurrentUserAccessToken } from "store/user/user.reducer";

const MAX_ATTEMPTS = 1;

const AuthPage = ({ location: routerLocation }: RouteComponentProps) => {
	const dispatch = useDispatch();

	const [getTokensAttempts, setGetTokenAttempts] = useState<number>(0);

	const location = window.location;
	const urlParams = location.search;

	const redirectPathFromState = (
		routerLocation.state as { [key: string]: any }
	)?.from.pathname;

	const urlData = useMemo(() => new URLSearchParams(urlParams), [urlParams]);
	const authCode = urlData.get(AuthParams.authCode);
	const authUserState = urlData.get(AuthParams.authUserState);
	const isRedirectToLogin = urlData.has(AuthParams.authLogin);
	const redirectPathFromURI = urlData.get(AuthParams.authPath);

	const token: string | null = useSelector(getCurrentUserAccessToken);

	const isTokenExpired = checkTokenExpiration();

	const { mutate: getTokens, isLoading: isTokensLoading } =
		useFusionAuthTokenMutation({
			onSuccess: () => urlData.delete(AuthParams.authCode),
			onError: () =>
				setGetTokenAttempts(currentValue => currentValue + 1),
		});

	useEffect(() => {
		if (isTokensLoading || getTokensAttempts >= MAX_ATTEMPTS) {
			return;
		}

		if (token) {
			if (authUserState === AuthUserState.Authenticated) {
				location.replace(
					`${location.origin}${redirectPathFromURI ?? ""}`,
				);
			} else if (authCode) {
				getTokens({ authCode, redirectToPath: redirectPathFromURI });
			} else if (isRedirectToLogin || isTokenExpired) {
				fusionAuthService.goToAuthorize(redirectPathFromState);
			} else {
				location.replace(
					`${location.origin}${redirectPathFromURI ?? ""}`,
				);
			}
		} else {
			if (authCode) {
				getTokens({ authCode, redirectToPath: redirectPathFromURI });
			} else {
				dispatch(uiActions.hideSessionExpirationModal());

				fusionAuthService.goToAuthorize(redirectPathFromState);
			}
		}
	}, [
		authCode,
		authUserState,
		dispatch,
		getTokens,
		getTokensAttempts,
		isRedirectToLogin,
		isTokenExpired,
		isTokensLoading,
		location,
		redirectPathFromState,
		redirectPathFromURI,
		token,
		urlData,
		urlParams,
	]);

	return null;
};

export default AuthPage;
